import { google } from 'global';
import { createAdvertisement } from '../utils/models';
import { METADATA_TYPE_AD_VAST, EVENT_COMPLETE, EVENT_TRACKING_ACTION_START, EVENT_PLAYING, VERSION } from '../constants';
import { Tracker } from '../tracking';
import { url_query_get } from '../../utils/url';

/**
 * Create a player ad from an IMA SDK ad object
 *
 * @param  {google.ima.Ad} ad IMA SDK ad event
 *
 * @return {Object}    A player ad object
 */
function createAdFromIMA (ad) {
  const metadata = {
    uid: ad.getAdId(),
    contentType: METADATA_TYPE_AD_VAST,
    creator: ad.getAdvertiserName(),
    title: ad.getTitle(),
    description: ad.getDescription(),
    duration: ad.getDuration(),
    vastCreativeId: ad.getCreativeId(),
    url: ad.g.clickThroughUrl
  };

  const selectionSettings = new google.ima.CompanionAdSelectionSettings();
  selectionSettings.creativeType = google.ima.CompanionAdSelectionSettings.CreativeType.IMAGE;
  selectionSettings.resourceType = google.ima.CompanionAdSelectionSettings.ResourceType.STATIC;
  selectionSettings.sizeCriteria = google.ima.CompanionAdSelectionSettings.SizeCriteria.IGNORE;

  let companions = [];

  try {
    companions = ad.getCompanionAds(0, 0, selectionSettings)
      .map(function (companionAd) {
        return {
          type: companionAd.g.resourceType.toLowerCase(),
          url: companionAd.g.resourceValue,
          content: companionAd.getContent(),
          width: companionAd.getWidth(),
          height: companionAd.getHeight()
        };
      });
  } catch (e) {
    // the sdk doesn't provide default values for companions when the response has no companions
    // and this causes an exception to be thrown, so we catch it here and just set our
    // companions to an empty list
    companions = [];
  }

  const audio = {
    url: ad.getMediaUrl(),
    bitrate: ad.getVastMediaBitrate(),
    mime: ad.getContentType()
  };

  // clickthrough links
  const links = [];

  const playerAd = createAdvertisement({
    metadata: metadata,
    audio: [audio],
    companions: companions,
    links: links
  });

  return playerAd;
}

export default class IMA {
  constructor (advertising) {
    if (!IMA.isSupported()) {
      throw new Error('Cannot start the IMA ad provider: IMA SDK not loaded or not supported');
    }

    this.logger = advertising.logger;

    this._advertising = advertising;
    this._contentPlayer = advertising.content;
    this._inAdBreak = false;
    this._initialized = false;
    this._schedule = [];

    this.onContentComplete = this.onContentComplete.bind(this);
  }

  /**
   * Check if the sdk is currently playing an ad
   *
   * @return {Boolean}
   */
  inAdBreak () {
    return this._inAdBreak;
  }

  init () {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    try {
      const customAdContainer = this._advertising.getAdContainer();
      const customClickTracker = this._advertising.getAdCustomClickTracker();

      this._adContainer = customAdContainer;
      this._adClickTracker = customClickTracker;

      this._adDisplayContainer = new google.ima.AdDisplayContainer(
        customAdContainer,
        this._contentPlayer.getMedia()
      );
      this._adDisplayContainer.initialize();

      this._adsLoader = new google.ima.AdsLoader(this._adDisplayContainer);
      this._adsManager = null;

      // enabled VPAID ads
      this._adsLoader.getSettings().setVpaidMode(google.ima.ImaSdkSettings.VpaidMode.ENABLED);
      // set the player attributes
      this._adsLoader.getSettings().setPlayerType('iono-player');
      this._adsLoader.getSettings().setPlayerVersion(VERSION);
      this._adsLoader.getSettings().setAutoPlayAdBreaks(true);

      // ads have been loaded, init the ad manager
      this._adsLoader.addEventListener(
        google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        this.onAdsManagerLoaded,
        false,
        this
      );

      // error loading ads
      this._adsLoader.addEventListener(
        google.ima.AdsManagerLoadedEvent.Type.AD_ERROR,
        this.onAdError,
        false,
        this
      );

      this._contentPlayer.on(EVENT_COMPLETE, this.onContentComplete);
    } catch (e) {
      this.logger.warn(`Failed to initialize IMA SDK: ${e.getMessage()}`);
    }
  }

  /**
   * Load a new ad schedule
   *
   * @param  {Array} schedule An array of player ad schedule objects
   */
  load (schedule) {
    this.logger.debug('IMA: scheduling ads');
    this._schedule = schedule;
    this.destroyAdsManager();
  }

  /**
   * Destroy the IMA instance
   */
  destroy () {
    this._contentPlayer.off(EVENT_COMPLETE, this.onContentComplete);

    this.destroyAdsManager();

    if (this._adsLoader) {
      this._adsLoader.removeEventListener(
        google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        this.onAdsManagerLoaded,
        false,
        this
      );
      this._adsLoader.removeEventListener(
        google.ima.AdErrorEvent.Type.AD_ERROR,
        this.onAdsLoaderError,
        false,
        this
      );

      this._adsLoader.destroy();
      this._adsLoader = null;
    }

    if (this._adDisplayContainer) {
      this._adDisplayContainer.destroy();
      this._adDisplayContainer = null;
      this._adDisplayContainerInitialized = false;
    }

    this._adClickTracker = null;
    this._currentAd = null;
    this._currentPlayerAd = null;
    this._inAdBreak = false;
    this._initialized = false;
  }

  /**
   * Begin the preroll sequence
   *
   * @return {Promise} Promise that resolves to a boolean indicating the presence of a preroll
   */
  startPreroll () {
    if (this._schedule.length === 0) {
      return Promise.resolve(false);
    }

    this.init();
    if (!this._adsLoader) {
      return Promise.resolve(false);
    }

    // for now we only ever try the first ad in the schedule
    this._currentPlayerAd = this._schedule[0];
    this.requestAds(this._currentPlayerAd.metadata.url);

    return new Promise((resolve, reject) => {
      const removeEventListeners = _ => {
        this._adsLoader.removeEventListener(
          google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
          onAdsManagerLoaded,
          false,
          this
        );
        this._adsLoader.removeEventListener(
          google.ima.AdErrorEvent.Type.AD_ERROR,
          onAdsLoaderError,
          false,
          this
        );
      };

      // wait for the ads manager to load before checking for preroll ads
      // if any prerolls exist, then an ad break starts otherwise
      // content should resume playback
      const onAdsManagerLoaded = (event) => {
        removeEventListeners();
        // check if there are prerolls
        const cuePoints = this._adsManager.getCuePoints();
        if (cuePoints.indexOf(0) !== -1) {
          this.onAdBreakStart();
          resolve(true);
        } else {
          resolve(false);
        }
      };
      this._adsLoader.addEventListener(
        google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        onAdsManagerLoaded,
        false,
        this
      );

      // resume usual content playback on error
      const onAdsLoaderError = (event) => {
        removeEventListeners();
        resolve(false);
      };
      this._adsLoader.addEventListener(
        google.ima.AdErrorEvent.Type.AD_ERROR,
        onAdsLoaderError,
        false,
        this
      );
    });
  }

  /**
   * Begin a postroll ad break
   *
   * @return {Promise} Promise that resolves to a boolean indicating the presence of a postroll
   */
  startPostroll () {
    const cuePoints = (this._adsManager && this._adsManager.getCuePoints()) || [];
    if (cuePoints.indexOf(-1) !== -1) {
      this.onAdBreakStart();
      return Promise.resolve(true);
    }
    return Promise.resolve(false);
  }

  /**
   * Retrieve the currently playing ad as an IMA SDK ad
   *
   * @return {Object}
   */
  getCurrentAd () {
    return this._currentAd;
  }

  /**
   * Retrieve the currently playing ad as a player ad
   *
   * @return {Object}
   */
  getCurrentPlayerAd () {
    return this._currentPlayerAd;
  }

  /**
   * Retrieve the duration of the current ad
   *
   * @return {Number}
   */
  getDuration () {
    if (this._currentAd) {
      return this._currentAd.getDuration();
    }
    return 0;
  }

  /**
   * Retrieve the time remaining for the current ad
   *
   * @return {Number}
   */
  getRemainingTime () {
    if (this._adsManager) {
      return this._adsManager.getRemainingTime();
    }
    return this.getDuration();
  }

  /**
   * Retrieve the position of the current ad
   *
   * @return {Number}
   */
  getPosition () {
    const remainingTime = this.getRemainingTime();
    if (remainingTime < 0) {
      return 0;
    }
    return this.getDuration() - remainingTime;
  }

  /**
   * Retrieve the offet after which the current ad can be skipped
   *
   * @return {Number}
   */
  getSkipOffset () {
    if (this._currentAd) {
      return this._currentAd.getSkipTimeOffset();
    }
    return -1;
  }

  /**
   * Retrieve the playback source of the currently playing ad
   *
   * @return {Object}
   */
  getSource () {
    if (this._currentPlayerAd) {
      return this._currentPlayerAd.audio[0];
    }
  }

  /**
   * Check if the current ad can be skipped
   *
   * @return {Boolean}
   */
  isSkippable () {
    if (this._adsManager) {
      return this._adsManager.getAdSkippableState();
    }
    return false;
  }

  /**
   * Request ads from an ads server by adTagUrl
   *
   * @param  {String} adTagUrl The adTagUrl to be requested
   */
  requestAds (adTagUrl) {
    this.init();

    this.logger.debug(`IMA: requesting ad from '${adTagUrl}'`);

    if (url_query_get(adTagUrl, 'ad_type') !== 'audio') {
      this.logger.warn(`IMA: loading an adTagUrl that does NOT specify ad_type=audio, if this ads creative has visuals they may not be visible`);
    }

    this._contentCompletedCalled = false;
    this._allAdsCompeleted = false;
    this._adTagUrl = adTagUrl;

    const adsRequest = new google.ima.AdsRequest();

    adsRequest.adTagUrl = adTagUrl;
    adsRequest.vastLoadTimeout = this._advertising.options.timeout;
    adsRequest.linearAdSlotWidth = this._adContainer.clientWidth;
    adsRequest.linearAdSlotHeight = this._adContainer.clientHeight;
    adsRequest.nonLinearAdSlotWidth = adsRequest.linearAdSlotWidth;
    adsRequest.nonLinearAdSlotHeight = adsRequest.linearAdSlotHeight;

    adsRequest.setContinuousPlayback(true);

    this._adsLoader.requestAds(adsRequest);
  }

  /**
   * Pause the currently playing ad
   */
  pause () {
    if (this._adsManager) {
      this._adsManager.pause();
    }
  }

  /**
   * Resume the current ad
   */
  resume () {
    if (this._adsManager) {
      this._adsManager.resume();
    }
  }

  /**
   * Resize the current ad container
   *
   * @param  {Number} width  New width of the ad container
   * @param  {Number} height New height of the ad container
   */
  resize (width, height) {
    if (this._adsManager) {
      this._adsManager.resize(width, height, google.ima.ViewMode.NORMAL);
    }
  }

  /**
   * Skip the currently playing ad
   */
  skip () {
    if (this._adsManager) {
      this._adsManager.skip();
    }
  }

  /**
   * Destroy the current ads manager
   */
  destroyAdsManager () {
    if (this._adsManager) {
      this.logger.debug('IMA: destroying ads manager');

      this._adsManager.destroy();
      this._adsManager = null;
    }
  }

  /**
   * Start an ads manager instance
   *
   * @param  {google.ima.AdsManager} adsManager An instance of the IMA SDK ads manager
   */
  startAdsManager (adsManager) {
    this.logger.debug('IMA: starting ads manager');

    adsManager.addEventListener(
      google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.onContentPauseRequested,
      false,
      this
    );
    adsManager.addEventListener(
      google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.onContentResumeRequested,
      false,
      this
    );
    adsManager.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      this.onAdError,
      false,
      true
    );
    adsManager.addEventListener(
      google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      this.onAllAdsCompleted,
      false,
      this
    );
    adsManager.addEventListener(
      google.ima.AdEvent.Type.COMPLETE,
      this.onAdComplete,
      false,
      this
    );
    adsManager.addEventListener(
      google.ima.AdEvent.Type.LOADED,
      this.onAdLoaded,
      false,
      this
    );
    adsManager.addEventListener(
      google.ima.AdEvent.Type.STARTED,
      this.onAdStarted,
      false,
      this
    );
    adsManager.addEventListener(
      google.ima.AdEvent.Type.AD_PROGRESS,
      this.onAdProgress,
      false,
      this
    );
    adsManager.addEventListener(
      google.ima.AdEvent.Type.SKIPPED,
      this.onAdSkipped,
      false,
      this
    );
    adsManager.addEventListener(
      google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
      this.onAdSkipStateChanged,
      false,
      this
    );

    adsManager.init(this._adContainer.clientWidth, this._adContainer.clientHeight, google.ima.ViewMode.NORMAL);
    adsManager.start();
  }

  /**
   * Called when an ad break starts
   */
  onAdBreakStart () {
    this.logger.debug('IMA: ad break started');
    this._inAdBreak = true;
    this._advertising.onAdBreakStart();
  }

  /**
   * Called when an ad break ends
   */
  onAdBreakEnd () {
    this.logger.debug('IMA: ad break ended');
    this._inAdBreak = false;
    this._advertising.onAdBreakEnd();
  }

  /**
   * Handle ad error events
   *
   * @param  {google.ima.AdErrorEvent} adErrorEvent An ad error event triggered by the IMA SDK
   */
  onAdError (adErrorEvent) {
    this.logger.error('IMA: ' + adErrorEvent.getError().getMessage());

    this.destroyAdsManager();

    this._inAdBreak = false;
    this._contentPlayer.play();
  }

  /**
   * Handle event triggered when an ad is loaded by the IMA SDK
   *
   * @param  {google.ima.AdEvent} adEvent An ad loaded event triggered by the IMA SDK
   */
  onAdLoaded (adEvent) {
    this.logger.debug('IMA: ad loaded');
    if (!adEvent.getAd().isLinear()) {
      this.onContentResumeRequested();
    }
  }

  /**
   * Handle event triggered when an ad is skipped by the IMA SDK
   *
   * @param  {google.ima.AdEvent} adEvent The event triggered by the IMA SDK
   */
  onAdSkipped (adEvent) {
    this.logger.debug('IMA: ad skipped');
    const skippedAd = this._currentPlayerAd;
    this._currentAd = null;
    this._currentPlayerAd = null;
    this._currentPlayerTracker = null;
    this._advertising.onAdSkip(skippedAd);
  }

  /**
   * Handle event triggered when the skippable status of the current ad changes
   *
   * @param  {google.ima.AdEvent} adEvent The event triggered by the IMA SDK
   */
  onAdSkipStateChanged (adEvent) {
    if (this._adsManager && this._adsManager.getAdSkippableState()) {
      this.logger.debug('IMA: ad became skippable');
      this._advertising.onAdSkippable();
    }
  }

  /**
   * Handle event triggered when an ad is completed by the IMA SDK
   *
   * @param  {google.ima.AdEvent} adEvent The event triggered by the IMA SDK
   */
  onAdComplete (adEvent) {
    this.logger.debug('IMA: ad complete');

    const ad = adEvent.getAd();
    const adPodInfo = ad.getAdPodInfo();
    const playerAd = this._currentPlayerAd;

    this._currentAd = null;
    this._currentPlayerAd = null;
    this._currentPlayerTracker = null;

    this._advertising.onAdEnd(playerAd);

    // Bumper ads are short linear ads that can indicate to a user when the
    // user is entering into or exiting from an ad break.
    const isBumperAd = adPodInfo.getIsBumper();
    // Is this the last ad in the ad break
    const isLastAd = adPodInfo.getAdPosition() === adPodInfo.getTotalAds();

    if (isBumperAd || isLastAd) {
      this.onAdBreakEnd();
    }
  }

  /**
   * Handle event triggered when an ads current position is updated
   *
   * @param  {google.ima.AdEvent} adEvent The event triggered by the IMA SDK
   */
  onAdProgress (adEvent) {
    this._advertising.onAdTimeUpdate();
  }

  /**
   * Handle event triggered when an ad is started by the IMA SDK
   *
   * @param  {google.ima.AdEvent} adEvent The event triggered by the IMA SDK
   */
  onAdStarted (adEvent) {
    if (!this.inAdBreak()) {
      this.onAdBreakStart();
    }

    this.logger.debug('IMA: ad started');

    this._currentAd = adEvent.getAd();
    this._currentPlayerAd = createAdFromIMA(this._currentAd);

    this._advertising.onAdStart(this._currentPlayerAd);

    // create a tracker
    this._currentPlayerTracker = new Tracker(this._advertising, this._currentPlayerAd);

    // create the tracking context
    const trackingContext = this._advertising.getTrackingContext();

    // update trackign context with IMA specifics
    trackingContext.adcid = this._currentAd.getCreativeId();
    trackingContext.event = EVENT_TRACKING_ACTION_START;

    // track the start event for this ad
    this._currentPlayerTracker.track({ type: EVENT_PLAYING }, trackingContext);
  }

  /**
   * Handle ads manager loaded events
   * @param  {google.ima.AdsManagerLoadedEvent} adsManagerLoadedEvent An event triggered by the IMA SDK
   */
  onAdsManagerLoaded (adsManagerLoadedEvent) {
    this.logger.debug('IMA: ads manager loaded');

    try {
      const adsRenderingSettings = new google.ima.AdsRenderingSettings();
      adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;

      this._adsManager = adsManagerLoadedEvent.getAdsManager(
        this._contentPlayer.getMedia(),
        adsRenderingSettings
      );

      this.startAdsManager(this._adsManager);
    } catch (err) {
      this.logger.error('IMA: Failed to start ads manager: ' + err.getMessage());

      if (this._adsManager) {
        this.destroyAdsManager();
      }
      this._contentPlayer.play();
    }
  }

  /**
   * Handle event when all ads in the current scheuld have been played
   */
  onAllAdsCompleted () {
    this.logger.debug('IMA: all ads completed');
    this._allAdsCompeleted = true;
  }

  /**
   * Handle event when content playback completes
   */
  onContentComplete () {
    this._contentCompletedCalled = true;
    this._adsLoader.contentComplete();
  }

  /**
   * Handle event when IMA SDK is requesting content be paused for an ad break
   */
  onContentPauseRequested () {
    this.logger.debug('IMA: content pause requested');
    this._linearAdPlaying = true;
    this._contentPlayer.pause();
  }

  /**
   * Handle event when IMA SDK is returning to content playback
   */
  onContentResumeRequested () {
    this.logger.debug('IMA: content resume requested');
    this._linearAdPlaying = false;
    if (!this._contentCompletedCalled) {
      this._contentPlayer.play();
    }
  }

  /**
   * Check if the IMA SDK is supported on this environment
   *
   * @return {Boolean}
   */
  static isSupported () {
    return !!(google && google.ima);
  }
}
